'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ModalContentWrapper } from '../common/Modal';
import { http } from '../../lib/api/http';
import { V3, V4 } from '../../lib/api/urls';
import { RedButton } from '../styled/Button';
import { ModalState } from '../../models';
import { initialModalState } from '../../models/modal';
import Loading from '../animations/loading';
import { ACTION_TYPES } from '../../models/action-types';
import { Certificate, NotificationPopUp } from '../../models/reservations';
import { useAuthorization } from '../../providers/authorization';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../lib/api/browserStorage';

export function PaymentModal({
  status: queryStatus,
  setModalState,
  gbReccomendLink,
}: {
  status: string | string[] | undefined;
  setModalState: Dispatch<SetStateAction<ModalState>>;
  gbReccomendLink: string | null;
}) {
  const auth = useAuthorization();
  const [fetchStatus, setFetchStatus] = useState('loading');
  const [orderData, setOrderData] = useState<Certificate | null>(null);
  const [notificationPopup, setNotificationPopup] =
    useState<NotificationPopUp | null>(null);
  const router = useRouter();
  const MAX_ATTEMPT_FETCH_CERTIFICATES = 15;
  async function fetchReservation(
    reservationId: string
  ): Promise<Certificate | null> {
    let response: Certificate;
    try {
      response = await http.get(
        V3.reservationsItem(reservationId),
        null,
        null,
        {
          contentType: 'json',
          withTokenRequest: true,
        }
      );
      return await new Promise((resolve) =>
        // eslint-disable-next-line no-promise-executor-return
        setTimeout(() => resolve(response), 1000)
      );
    } catch (error) {
      return await new Promise((resolve) =>
        // eslint-disable-next-line no-promise-executor-return
        setTimeout(() => resolve(null), 1000)
      );
    }
  }

  function onClearReservationLocalStorage() {
    setLocalStorageItem(ACTION_TYPES.reservation, '');
  }

  function isCertificatesEqualToTicketsCount({
    count,
    certificates,
  }: {
    count: number[];
    certificates: { file: string | null }[];
  }) {
    const sum = count.reduce((a, b) => a + b);
    const numberOfFinishedCerts = certificates
      .map(({ file }) => file)
      .filter(Boolean).length;
    return sum === numberOfFinishedCerts;
  }

  useEffect(() => {
    async function fetch() {
      const reservationId = getLocalStorageItem(ACTION_TYPES.reservation);
      if (reservationId && queryStatus === 'success') {
        let fetchCount = 0;
        let orderResponse: Certificate | null = null;
        while (fetchCount < MAX_ATTEMPT_FETCH_CERTIFICATES) {
          // eslint-disable-next-line no-await-in-loop
          orderResponse = await fetchReservation(reservationId);
          let notificationPopupResponse = null;
          if (orderResponse?.event.notification_popup) {
            // eslint-disable-next-line no-await-in-loop
            notificationPopupResponse = await http.get(
              V4.notificationTemplates(orderResponse.event.notification_popup),
              null,
              null,
              {
                contentType: 'json',
                withTokenRequest: true,
              }
            );
          }
          if (notificationPopupResponse?.show_on_site) {
            setNotificationPopup(notificationPopupResponse);
          }
          setOrderData(orderResponse);
          if (
            orderResponse &&
            isCertificatesEqualToTicketsCount(orderResponse)
          ) {
            setFetchStatus('success');
            break;
          }
          fetchCount += 1;
        }
        if (
          !orderResponse ||
          !isCertificatesEqualToTicketsCount(orderResponse)
        ) {
          setFetchStatus('error');
        }
      }
    }
    fetch();
  }, []);
  return (
    <ModalContentWrapper>
      <div css={{ padding: '23px 30px 20px 30px' }}>
        <p css={{ fontSize: 18, color: '#333' }}>
          {fetchStatus === 'success' && (notificationPopup?.title || 'Успех')}
          {fetchStatus === 'error' && 'Ошибка'}
        </p>
      </div>
      <div css={{ padding: '14px 30px 30px 30px' }}>
        {fetchStatus === 'loading' && (
          <div
            css={{
              height: 230,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Loading>
              <img src='/img/throbber.png' />
            </Loading>
          </div>
        )}

        <p>
          {fetchStatus === 'success' &&
            orderData &&
            (notificationPopup?.message_html ? (
              <div
                css={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '1.43',
                }}
                dangerouslySetInnerHTML={{
                  __html: notificationPopup.message_html,
                }}
              />
            ) : (
              <>
                {orderData?.certificates?.length === 1 && (
                  <>
                    Покупка успешна, ваш сертификат отправлен на почту, можете
                    скачать его ниже
                  </>
                )}

                {orderData?.certificates?.length > 1 && (
                  <>
                    Покупка успешна, ваши сертификаты отправлены на почту,
                    можете скачать их ниже
                  </>
                )}
              </>
            ))}

          {queryStatus === 'error' && (
            <>Извините, покупка не удалась. Попробуйте еще раз</>
          )}

          {fetchStatus === 'error' && (
            <>
              Не удалось получить список сертификатов, проверьте их на почте{' '}
              {orderData?.email ||
                auth?.getUser()?.email ||
                '(указанной при бронировании)'}{' '}
              или свяжитесь с администрацией сайта
            </>
          )}
        </p>

        {fetchStatus === 'success' && <br />}

        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              gap: 10,
            },
          }}
        >
          {fetchStatus === 'success' &&
            !!orderData?.certificates?.length &&
            orderData.certificates.map((certificate) => (
              <a
                target='_blank'
                download
                href={certificate.file ?? undefined}
                key={certificate.file}
                css={{
                  width: 'fit-content',
                  color: '#f44336',
                  '&:hover': { color: 'rgba(244, 67, 54, 0.85)' },
                  [DEVICE_SCREENS_MAX_STRING.sm]: {
                    fontSize: 18,
                  },
                }}
                rel='noreferrer'
              >
                {certificate.file?.split('/')?.pop()}
              </a>
            ))}
        </div>

        {fetchStatus !== 'loading' && (
          <div
            css={{ marginTop: 28, display: 'flex', justifyContent: 'center' }}
          >
            <RedButton
              callback={() => {
                setModalState(initialModalState);
                onClearReservationLocalStorage();
                if (
                  notificationPopup?.show_button &&
                  notificationPopup.external_link &&
                  queryStatus === 'success'
                ) {
                  window?.open(notificationPopup.external_link, '_blank');
                  return;
                }
                if (gbReccomendLink && queryStatus === 'success') {
                  router.push(gbReccomendLink);
                }
              }}
            >
              {queryStatus === 'success' &&
                (notificationPopup?.show_button && notificationPopup.button_text
                  ? notificationPopup.button_text
                  : 'Отлично')}
              {queryStatus === 'error' && 'Закрыть'}
            </RedButton>
          </div>
        )}
      </div>
    </ModalContentWrapper>
  );
}
